// The studio's live agent: one controller that the big stage, the panel thumbnail and the newest chat message
// all follow, plus the demo chat, the event log, sample runs and the endless "Auto" conversation.
import { createController, STATE, type Controller, type Kind, type StateChange } from 'wobbli';
import { HOLIDAY, holidayOn, today } from 'wobbli/holidays';
import type { StudioSettings } from 'wobbli/formats';

export interface Msg { id: number; role: 'user' | 'ai'; text?: string; kind?: Kind; st?: string; v?: string; note?: string | null }
export interface LogEntry { t: string; name: string; st: string; v: string }

const PROMPTS = ['Which business units missed target?', 'Compare this quarter with last quarter', 'What changed in the forecast this week?', 'Summarise last month for my team'];
// Each step: [ms after the previous step, kind, argument]. Kinds: msg (the user writes), ev (an app event),
// prog (progress from 0 to 100% over the given ms), still (the long-wait switch).
type Step = [number, 'msg' | 'ev' | 'prog' | 'still', (string | number)?];
const SCENARIOS: { steps: Step[]; tail: number }[] = [
  { steps: [[0, 'msg', 'Summarise last month for my team'], [2600, 'ev', 'stream.start'], [2200, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Which business units missed target?'], [1200, 'ev', 'tool.query'], [300, 'prog', 3200], [3600, 'ev', 'tool.analyze'], [2800, 'ev', 'stream.start'], [2000, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Find last year’s board deck on churn'], [1000, 'ev', 'tool.search'], [3200, 'ev', 'stream.start'], [1800, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Turn this into a five-slide deck'], [1000, 'ev', 'tool.build'], [3000, 'ev', 'tool.export'], [2800, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Pull the latest numbers from the finance cube'], [1000, 'ev', 'tool.connect'], [2600, 'ev', 'error.network']], tail: 4200 },
  { steps: [[0, 'msg', 'How are we doing on the big one?'], [2200, 'ev', 'input.unclear'], [3600, 'msg', 'Revenue per member, this quarter'], [1000, 'ev', 'tool.query'], [2600, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Email the summary to the leadership team'], [1000, 'ev', 'stream.start'], [2400, 'ev', 'approval.needed'], [3400, 'msg', 'Yes, send it'], [800, 'ev', 'instruction.ack'], [2400, 'ev', 'tool.upload'], [2200, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Can someone from finance double-check this?'], [1200, 'ev', 'handoff']], tail: 3800 },
  { steps: [[0, 'msg', 'Rebuild the forecast with this year’s data'], [1000, 'ev', 'tool.analyze'], [3000, 'still'], [3800, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Run that for all 40 regions'], [1200, 'ev', 'error.rate_limit'], [3400, 'ev', 'tool.query'], [300, 'prog', 2800], [3200, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'ev', 'voice.start'], [3200, 'msg', 'What changed in the forecast this week?'], [1000, 'ev', 'tool.search'], [2600, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Here’s the CSV from the vendor'], [1000, 'ev', 'tool.import'], [3000, 'ev', 'tool.sync'], [2800, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Download the claims extract for Q3'], [1000, 'ev', 'tool.download'], [300, 'prog', 3000], [3400, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Open the dashboard I don’t have access to'], [1200, 'ev', 'error.permission']], tail: 4000 },
  { steps: [[0, 'ev', 'session.start'], [3000, 'msg', 'Morning! Anything new in the overnight numbers?'], [1000, 'ev', 'tool.query'], [2600, 'ev', 'stream.end']], tail: 2400 },
  { steps: [[0, 'msg', 'Thanks, that’s all for today'], [1400, 'ev', 'session.end']], tail: 3600 }
];
const pick = <T>(list: T[]) => list[Math.floor(Math.random() * list.length)];

export interface HolidayNow { key: string; costume: string }

export class StudioEngine {
  readonly controller: Controller;
  msgs: Msg[] = [{ id: 1, role: 'user', text: 'Summarise last month for my team' }, { id: 2, role: 'ai', kind: 'answer', st: 'idle', v: 'idle' }];
  log: LogEntry[] = [];
  /** Studio-only variant pins ("Ways to act" chips). */
  pins: Record<string, string> = {};
  running = false;
  auto = false;
  /** The costume "Pick one" shows when its costume is random. */
  pickCos: string | null = null;
  /** A date checked in the Holiday tab (day number), or null for today. */
  holDate: number | null = null;
  private n = 0;
  private version = 0;
  private listeners = new Set<() => void>();
  private timers: ReturnType<typeof setTimeout>[] = [];
  private seqTimers: ReturnType<typeof setTimeout>[] = [];
  private autoTimers: ReturnType<typeof setTimeout>[] = [];
  private deck: number[] = [];
  private lastScenario = -1;
  private settings: StudioSettings;

  constructor(settings: StudioSettings) {
    this.settings = settings;
    this.controller = createController({ ...this.controllerOptions(settings), onStateChange: ch => this.onChange(ch) });
  }

  // ---------- store plumbing for React ----------
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getVersion = () => this.version;
  private bump() { this.version++; this.listeners.forEach(fn => fn()); }

  private controllerOptions(s: StudioSettings) {
    return { events: s.events, speed: s.speed, motion: s.motion, fidgets: s.fidgets === 'some' ? 'sometimes' as const : s.fidgets, dozeAfter: s.dozeAfter, stillWorking: { after: s.waitAfter, state: s.waitState, variant: s.waitVar }, pins: this.pins };
  }
  applySettings(s: StudioSettings) {
    this.settings = s;
    this.controller.setOptions(this.controllerOptions(s));
    this.bump();
  }

  private lastAi() { for (let i = this.msgs.length - 1; i >= 0; i--) if (this.msgs[i].role === 'ai') return this.msgs[i]; return null; }
  private onChange(ch: StateChange) {
    const m = this.lastAi();
    if (m && ch.cause !== 'fidget') Object.assign(m, { st: ch.state, v: ch.variant, kind: ch.kind, note: ch.note });
    if (ch.cause === 'still-working') this.logEv('wait.long', ch.state, ch.variant);
    else if (ch.cause === 'doze') this.logEv('idle.timeout', ch.state, ch.variant);
    else if (ch.cause === 'wake' && ch.state === 'greet') this.logEv('user.back', ch.state, ch.variant);
    if (this.running && ch.state === 'idle' && ch.cause === 'back-to-idle') this.running = false;
    this.bump();
  }
  private logEv(name: string, st: string, v: string) {
    this.log = [{ t: new Date().toTimeString().slice(0, 8), name, st, v }, ...this.log].slice(0, 6);
  }

  // ---------- timers ----------
  private later(ms: number, fn: () => void) { this.timers.push(setTimeout(fn, ms)); }
  private clearTimers() { this.timers.forEach(clearTimeout); this.timers = []; }
  private clearSeq() { this.seqTimers.forEach(clearTimeout); this.seqTimers = []; }
  private resetFlow() { this.stopAuto(); this.clearTimers(); this.clearSeq(); this.running = false; }

  private newTurn(text: string) {
    const id = Date.now();
    this.msgs.forEach(m => { if (m.role === 'ai') { m.st = 'idle'; m.v = 'idle'; } });
    this.msgs.push({ id, role: 'user', text }, { id: id + 1, role: 'ai', kind: 'pending', st: 'idle', v: 'idle' });
    this.msgs = this.msgs.slice(-6);
  }

  /** "Play a turn": a message that gets an answer, fails, or is unclear. */
  run(kind: 'ok' | 'fail' | 'unclear', draft = '') {
    if (this.running) return;
    this.resetFlow();
    const fallback = kind === 'unclear' ? 'How are we doing on the big one?' : kind === 'fail' ? 'Pull the latest numbers from the finance cube' : PROMPTS[this.n % PROMPTS.length];
    this.newTurn(draft.trim() || fallback);
    if (kind === 'ok') this.n++;
    this.running = true;
    this.controller.setState('busy');
    if (kind === 'fail') this.later(3000, () => { this.running = false; this.controller.setState('sad'); });
    else if (kind === 'unclear') this.later(2600, () => { this.running = false; this.controller.setState('curious'); });
    else this.later(3600, () => this.controller.setState('happy'));
    this.bump();
  }
  /** Shows a state now ("Force a state", a gallery tile). */
  force(st: string, v?: string) {
    this.resetFlow();
    if (st === 'idle') this.controller.setState('idle');
    else this.controller.setState(st, v);
    const m = this.lastAi(); if (m) m.note = null;
    this.bump();
  }
  pinVariant(st: string, v: string) {
    if (v === 'random') delete this.pins[st]; else this.pins[st] = v;
    this.controller.setOptions({ pins: { ...this.pins } });
    this.force(st, v === 'random' ? undefined : v);
  }
  previewFidget(f: string) {
    this.stopAuto();
    if (f === 'doze') this.controller.doze(2500);
    else this.controller.fidget(f);
  }

  /** An app event, from the Events tab or a sample run. */
  emit(name: string, value?: unknown, fromSeq = false) {
    if (!fromSeq) { this.clearSeq(); this.stopAuto(); }
    this.clearTimers();
    this.running = false;
    if (name === 'message.sent') this.newTurn(typeof value === 'string' && value ? value : PROMPTS[this.n++ % PROMPTS.length]);
    if (name === 'progress') this.controller.progress(value as number | null);
    else {
      this.controller.emit(name, value);
      const after = this.controller.getState();
      this.logEv(name, after.state, after.variant);
    }
    this.bump();
  }
  private seq(steps: [number, string, unknown?][]) {
    this.resetFlow();
    steps.forEach(s => this.seqTimers.push(setTimeout(() => this.emit(s[1], s[2], true), s[0])));
  }
  sampleRun() {
    const steps: [number, string, unknown?][] = [[0, 'message.sent'], [1000, 'tool.search'], [2600, 'tool.query']];
    for (let i = 0; i <= 10; i++) steps.push([2900 + i * 300, 'progress', i / 10]);
    steps.push([6400, 'tool.build'], [8400, 'stream.start'], [10400, 'stream.end']);
    this.seq(steps);
  }
  slowRun() { this.seq([[0, 'message.sent'], [900, 'tool.query'], [900 + (this.settings.waitAfter + 4) * 1000, 'stream.end']]); }

  // ---------- auto: an endless conversation that walks through the states ----------
  private autoLater(ms: number, fn: () => void) { this.autoTimers.push(setTimeout(() => { if (this.auto) fn(); }, ms)); }
  startAuto() {
    this.resetFlow();
    this.auto = true;
    this.bump();
    this.autoNext(500);
  }
  stopAuto() {
    if (!this.auto) return;
    this.auto = false;
    this.autoTimers.forEach(clearTimeout); this.autoTimers = [];
    this.bump();
  }
  private nextScenario() {
    if (!this.deck.length) {
      this.deck = SCENARIOS.map((_x, i) => i);
      for (let i = this.deck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]]; }
      if (this.deck[this.deck.length - 1] === this.lastScenario) this.deck.unshift(this.deck.pop() as number);
    }
    this.lastScenario = this.deck.pop() as number;
    return SCENARIOS[this.lastScenario];
  }
  private autoStep(st: Step) {
    const [, kind, arg] = st;
    if (kind === 'msg') this.emit('message.sent', arg, true);
    else if (kind === 'ev') this.emit(arg as string, null, true);
    else if (kind === 'still') {
      const s = this.settings, v = STATE[s.waitState]?.variants.some(x => x.key === s.waitVar) ? s.waitVar : undefined;
      this.controller.setState(s.waitState, v);
      this.logEv('wait.long', this.controller.getState().state, this.controller.getState().variant);
    } else if (kind === 'prog') for (let i = 0; i <= 10; i++) this.autoLater((i * (arg as number)) / 10, () => this.emit('progress', i / 10, true));
  }
  private autoNext(gap: number) {
    // a quiet moment between turns, sometimes with a fidget
    this.autoLater(gap * 0.4, () => {
      const st = this.controller.getState();
      if (st.state === 'idle' && st.variant === 'idle' && Math.random() < 0.5) this.controller.fidget(pick(['fglance', 'fstretch', 'fblink', 'flook', 'fshuffle']));
    });
    this.autoLater(gap, () => {
      const sc = this.nextScenario();
      let t = 0;
      sc.steps.forEach(st => { t += st[0]; this.autoLater(t, () => this.autoStep(st)); });
      this.autoNext(t + sc.tail + 2500 + Math.random() * 2500);
    });
  }

  // ---------- holidays ----------
  /** The costume the studio look wears now (or on the checked date). */
  holiday(s: StudioSettings): HolidayNow | null {
    if (s.holMode === 'off') return null;
    if (s.holMode === 'pick') {
      const h = HOLIDAY[s.pickHol];
      if (!h) return null;
      if (s.cosPin !== 'random') return { key: h.key, costume: s.cosPin };
      if (!this.pickCos || !h.costumes.some(c => c.key === this.pickCos)) this.pickCos = pick(h.costumes).key;
      return { key: h.key, costume: this.pickCos };
    }
    const a = holidayOn(s.region, this.holDate ?? today(), s.lead);
    return a ? { key: a.key, costume: a.costume } : null;
  }
  /** A new random costume for "Pick one", never the same twice in a row. */
  repickCostume(key: string) {
    const h = HOLIDAY[key];
    if (!h) return;
    this.pickCos = pick(h.costumes.filter(c => c.key !== this.pickCos)).key;
    this.bump();
  }
  setHolDate(d: number | null) { this.holDate = d; this.bump(); }

  /** Restores the live state from a share link. */
  restore(state: string, variant: string) {
    this.resetFlow();
    this.controller.setState(state, variant);
    this.bump();
  }

}
